import { newId, nowIso } from './ids.js'
import type { BoardSnapshot } from './schemas.js'

export interface NewBoardInput {
  /** Client-generated so a lost create response can be retried without duplicating the board. */
  id?: string
  name: string
  now?: string
}

/** A new board starts with one column, one swimlane and one row, and no cards. */
export function createBoardSnapshot(input: NewBoardInput): BoardSnapshot {
  const now = input.now ?? nowIso()
  const boardId = input.id ?? newId()
  const swimlaneId = newId()
  return {
    board: { id: boardId, name: input.name.trim(), revision: 0, createdAt: now, updatedAt: now },
    columnGroups: [],
    columns: [{ id: newId(), boardId, title: 'New column', groupId: null, position: 0 }],
    swimlanes: [{ id: swimlaneId, boardId, title: 'New swimlane', position: 0 }],
    rows: [{ id: newId(), boardId, swimlaneId, title: 'New row', position: 0 }],
    cards: [],
  }
}

/**
 * An independent copy of a board, for creating it again as a new board: every id is
 * replaced and every reference follows (fixtures have shared deterministic ids, and an
 * exported board keeps its original ones). Revision and timestamps start again.
 */
export function copyBoardSnapshot(snapshot: BoardSnapshot): BoardSnapshot {
  const ids = new Map<string, string>()
  const freshId = (id: string): string => {
    if (!ids.has(id)) ids.set(id, newId())
    return ids.get(id)!
  }
  const boardId = freshId(snapshot.board.id)
  const now = nowIso()
  return {
    board: { ...snapshot.board, id: boardId, revision: 0, createdAt: now, updatedAt: now },
    columnGroups: snapshot.columnGroups.map((group) => ({ ...group, id: freshId(group.id), boardId })),
    columns: snapshot.columns.map((column) => ({
      ...column, id: freshId(column.id), boardId,
      groupId: column.groupId === null ? null : freshId(column.groupId),
    })),
    swimlanes: snapshot.swimlanes.map((lane) => ({ ...lane, id: freshId(lane.id), boardId })),
    rows: snapshot.rows.map((row) => ({
      ...row, id: freshId(row.id), boardId, swimlaneId: freshId(row.swimlaneId),
    })),
    cards: snapshot.cards.map((card) => ({
      ...card, id: freshId(card.id), boardId,
      rowId: freshId(card.rowId), columnId: freshId(card.columnId),
      ...(card.relationships
        ? { relationships: card.relationships.map((relationship) => ({ ...relationship, targetId: freshId(relationship.targetId) })) }
        : {}),
      createdAt: now, updatedAt: now,
    })),
  }
}
